<template>
    <div class="col-lg-9">
        <!-- EVENT DETAILS SECTION -->
        <div class="card card-body border-0 mb-5">
            <h6 class="font-weight-bold mb-4 border-bottom pb-2">Event Details</h6>
            <!-- Success Banner for Event Details -->
            <transition name="fade">
                <div v-if="eventCreated" class="alert alert-success text-center p-3">
                    <h5 class="mb-1">Event Created Successfully!</h5>
                    <p>Your event details have been saved.</p>
                </div>
            </transition>
            <!-- Event Form -->
            <div>
                <div class="form-group">
                    <label>Event Image</label>
                    <file-upload
                        fileType="image"
                        :croppedImageHeight="350"
                        :aspectRatio="1"
                        v-model="eventForm.image"
                    ></file-upload>
                    <div v-text="eventForm.errors.image" class="text-danger font-12"></div>
                </div>
                <div class="form-group">
                    <label>Event Name</label>
                    <input
                        type="text"
                        v-model="eventForm.name"
                        class="form-control"
                        placeholder="Enter event name"
                        @input="onEventNameInput"
                    />
                    <div v-text="eventForm.errors.name" class="text-danger font-12"></div>
                </div>
                <div class="form-group">
                    <label>Event URL</label>
                    <input
                        type="text"
                        v-model="eventForm.slug"
                        class="form-control"
                        placeholder="event-url-slug"
                    />
                    <div v-text="eventForm.errors.slug" class="text-danger font-12"></div>
                    <small class="text-muted" v-if="eventUrlPreview">
                        Preview: {{ eventUrlPreview }}
                    </small>
                </div>
              <!-- right above the Event Name field: -->
              <div class="form-group">
                <label>Event Type</label>
                <select v-model="eventForm.event_type" class="form-control">
                  <option disabled value="">-- Select Event Type --</option>
                  <option value="PAID">Paid</option>
                  <option value="FREE">Free</option>
                </select>
                <div v-text="eventForm.errors.event_type" class="text-danger font-12"></div>
              </div>

              <div class="form-group">
                    <label>Event Description</label>
                    <textarea
                        v-model="eventForm.description"
                        class="form-control"
                        placeholder="Enter event description"
                    ></textarea>
                    <div v-text="eventForm.errors.description" class="text-danger font-12"></div>
                </div>
                <div class="form-group">
                    <label>Category</label>
                    <select v-model="eventForm.category" class="form-control">
                        <option selected value="" disabled>-- Select Category --</option>
                        <option v-for="c in eventCategories" :key="c" :value="c">{{ c }}</option>
                    </select>
                    <div v-text="eventForm.errors.category" class="text-danger font-12"></div>
                </div>
                <div class="form-group">
                    <label>Venue</label>
                    <input
                        type="text"
                        v-model="eventForm.venue"
                        class="form-control"
                        placeholder="Enter venue"
                    />
                    <div v-text="eventForm.errors.venue" class="text-danger font-12"></div>
                </div>
                <div class="form-group">
                    <label>Date</label>
                    <input type="date" v-model="eventForm.event_at" class="form-control" />
                    <div v-text="eventForm.errors.event_at" class="text-danger font-12"></div>
                </div>
                <div class="form-group">
                    <label>Time</label>
                    <input type="time" v-model="eventForm.time" class="form-control" />
                    <div v-text="eventForm.errors.time" class="text-danger font-12"></div>
                </div>
                <!-- Terms and Submit Button -->
                <div class="form-check mt-3">
                    <input
                        type="checkbox"
                        class="form-check-input"
                        id="agreeTerms"
                        v-model="agreeToTerms"
                    >
                    <label class="form-check-label" for="agreeTerms">
                        I have reviewed and agree to the <a href="/events-terms" target="_blank">Terms and Conditions</a>.
                    </label>
                </div>
                <button
                    class="btn btn-primary btn-block my-4"
                    :class="{ 'btn-loading': loading }"
                    :disabled="!agreeToTerms || loading"
                    @click="submitEventDetails"
                >
                    Save Event Details
                </button>
            </div>
        </div>

        <!-- TICKET TYPES SECTION -->
          <div v-if="eventForm.event_type === 'PAID'" class="card card-body border-0">
            <!-- Info Banner if event details not saved yet -->
            <div v-if="!eventCreated" class="alert alert-info text-center p-3 mb-3">
                Please save your event details above before creating ticket types.
            </div>
            <!-- Ticket Types Form -->
            <div>
                <!-- Success Banner for Ticket Types -->
                <transition name="fade">
                    <div v-if="ticketCreated" class="alert alert-success text-center p-4 mt-4">
                        <h5 class="mb-1">Ticket Types Created Successfully!</h5>
                        <p>Your ticket types have been saved. Redirecting to the events listing...</p>
                    </div>
                </transition>
                <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                    <h6 class="font-weight-bold mb-0">Create Ticket Types</h6>
                    <button
                        class="btn btn-sm btn-primary"
                        @click="openTicketModal"
                        :disabled="!eventCreated"
                    >
                        Add New Ticket Type
                    </button>
                </div>
                <div class="card mb-4 border-0">
                    <ul class="list-group list-group-flush">
                        <li
                            v-for="(ticket, index) in tickets"
                            :key="index"
                            class="list-group-item d-flex justify-content-between align-items-center"
                        >
              <span>
                {{ ticket.name }} ({{ ticket.type }}) - ${{ ticket.price }}
                ({{ ticket.quantity }} available)
              </span>
                            <div>
                                <i
                                    class="fas fa-pencil-alt text-secondary cursor-pointer"
                                    @click="editTicket(index)"
                                ></i>
                                <i
                                    class="fas fa-trash-alt text-danger ml-3 cursor-pointer"
                                    @click="removeTicket(index)"
                                ></i>
                            </div>
                        </li>
                        <li v-if="tickets.length === 0" class="list-group-item text-center">
                            No tickets added yet.
                        </li>
                    </ul>
                </div>
                <button
                    class="btn btn-primary btn-block my-4"
                    :class="{ 'btn-loading': loading }"
                    @click="createTicketType"
                    :disabled="!eventCreated"
                >
                    Save Ticket Types
                </button>
            </div>
        </div>

        <!-- TICKET MODAL -->
        <transition name="fade">
            <div
                v-if="showTicketModal"
                class="modal"
                tabindex="-1"
                style="display: block; background: rgba(0,0,0,0.5);"
            >
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">
                                {{ isEditing ? 'Edit Ticket' : 'Add New Ticket Type' }}
                            </h5>
                            <button type="button" class="close" @click="closeTicketModal">&times;</button>
                        </div>
                        <div class="modal-body">
                            <!-- Ticket Type Name -->
                            <div class="form-group">
                                <label>Type Name</label>
                                <select v-model="ticketForm.name" class="form-control">
                                    <option selected disabled>-- Select Ticket Type --</option>
                                    <option v-for="type in ticketTypes" :key="type">{{ type }}</option>
                                </select>
                                <div v-text="ticketForm.errors.name" class="text-danger font-12"></div>
                            </div>
                            <!-- Price -->
                            <div class="form-group">
                                <label>Price (USD)</label>
                                <input
                                    type="number"
                                    v-model="ticketForm.price"
                                    class="form-control"
                                    placeholder="Enter price"
                                />
                                <div v-text="ticketForm.errors.price" class="text-danger font-12"></div>
                            </div>
                            <!-- Description -->
                            <div class="form-group">
                                <label>Description</label>
                                <textarea
                                    v-model="ticketForm.description"
                                    class="form-control"
                                    placeholder="Enter description"
                                ></textarea>
                                <div v-text="ticketForm.errors.description" class="text-danger font-12"></div>
                            </div>
                            <!-- Ticket Type Selection -->
                            <div class="form-group">
                                <label>Ticket Type</label>
                                <select v-model="ticketForm.type" class="form-control">
                                    <option selected value="" disabled>-- Select Type --</option>
                                    <option value="1-Day Ticket">1-Day Ticket</option>
                                    <option value="Multi-Day Ticket">Multi-Day Ticket</option>
                                </select>
                                <div v-text="ticketForm.errors.type" class="text-danger font-12"></div>
                            </div>
                            <!-- Number of Days (conditional) -->
                            <div v-if="ticketForm.type === 'Multi-Day Ticket'" class="form-group">
                                <label>Number of Days</label>
                                <input
                                    type="number"
                                    v-model="ticketForm.days"
                                    class="form-control"
                                    placeholder="Enter number of days"
                                />
                                <div v-text="ticketForm.errors.days" class="text-danger font-12"></div>
                            </div>
                            <!-- Quantity -->
                            <div class="form-group">
                                <label>Quantity</label>
                                <input
                                    type="number"
                                    v-model="ticketForm.quantity"
                                    class="form-control"
                                    placeholder="Enter quantity"
                                />
                                <div v-text="ticketForm.errors.quantity" class="text-danger font-12"></div>
                            </div>
                            <div class="form-group">
                                <label>Features (one per line)</label>
                                <textarea
                                    v-model="ticketForm.features"
                                    class="form-control"
                                    placeholder="Enter features, one per line. Leave empty for default features."
                                    rows="3"
                                ></textarea>
                            </div>
                            <div class="form-group">
                                <label>Instructions (one per line)</label>
                                <textarea
                                    v-model="ticketForm.instructions"
                                    class="form-control"
                                    placeholder="Enter instructions, one per line. Leave empty for default instructions."
                                    rows="3"
                                ></textarea>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-sm btn-secondary" @click="closeTicketModal">
                                Cancel
                            </button>
                            <button type="button" class="btn btn-sm btn-primary" @click="saveTicket">
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import FileUpload from "../components/FileUpload.vue";
import axios from "axios";

export default {
    components: { FileUpload },
    data() {
        return {
            agreeToTerms: false,
            eventCreated: false,
            ticketCreated: false,
            loading: false,
            showPrompt: false,
            promptChoice: null,
            // Event form with errors as a plain object
            eventForm: {
                image: {},
                name: "",
                slug: "",
                event_type: "",        // ← NEW
                description: "",
                category: "",
                venue: "",
                event_at: "",
                time: "",
                errors: {}
            },
            // Ticket form with errors as a plain object
            ticketForm: {
                event_id: null,
                tickets: [],
                name: "",
                type: "",
                price: "",
                description: "",
                quantity: "",
                days: 0,
                features: "",
                instructions: "",
                errors: {}
            },
            tickets: [],
            eventCategories: [
                "Music",
                "Comedy",
                "Theater",
                "Film",
                "Sports",
                "Motorsport",
                "Workshops",
                "Conferences",
                "Festivals",
                "Exhibitions",
                "Community Events",
                "Health and Wellness",
                "Technology",
                "Religious/Spiritual",
                "Corporate",
                "Virtual Events",
                "Private Parties",
                "Food and Drink"
            ],
            ticketTypes: [
                "General Admission",
                "Standard",
                "VIP",
                "VVIP",
                "Early Bird",
                "Student",
                "Child",
                "Senior",
                "Group",
                "Season Pass",
                "Day Pass",
                "Weekend Pass",
                "Reserved Seating",
                "Standing Room",
                "Premium",
                "Backstage Access",
                "Meet and Greet",
                "Livestream Access",
                "All-Access Pass",
                "Corporate Package"
            ],
            showTicketModal: false,
            isEditing: false,
            editingIndex: null,
            // Optional custom details
            optionalDetails: {
                features: [],
                instructions: []
            },
            defaultFeatures: [
                'Ticket is non-refundable/exchangeable should you miss the event.',
                'Ticket cannot be replaced if lost or stolen or damaged.',
                'Ticket Expires after the day of the event.',
                'Delivery via SMS and/or email.'
            ],
            defaultInstructions: [
                'On the day of the event present your QR code at the entrance. Your ticket will be scanned for access.'
            ],
            // Editing state for optional details
            editingOptional: false,
            customFeaturesText: '',
            customInstructionsText: ''
        };
    },
    computed: {
        eventUrlPreview() {
            const slug = this.eventForm.slug || this.slugify(this.eventForm.name);
            if (!slug) return '';
            return `https://magetsi.co.zw/tickets/${slug}`;
        },
    },
    methods: {
        slugify(text) {
            return String(text || '')
                .normalize('NFKD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '')
                || '';
        },
        onEventNameInput() {
            if (this.eventCreated && this.eventForm.slug) {
                return;
            }
            this.eventForm.slug = this.slugify(this.eventForm.name);
        },
        formatForStorage(text) {
            // Split by newlines, trim each line, remove empty lines
            const lines = text.split('\n')
                .map(line => line.trim())
                .filter(line => line.length > 0);

            // Convert to clean JSON string without extra slashes
            return JSON.stringify(lines);
        },
      validateEventForm() {
        let isValid = true;
        this.eventForm.errors = {};  // Clear existing errors before validation

        // Validate all required fields
        if (!this.eventForm.image || this.eventForm.image === '') {
          this.$set(this.eventForm.errors, "image", "Event image is required.");
          isValid = false;
        }
        if (!this.eventForm.name) {
          this.$set(this.eventForm.errors, "name", "Event name is required.");
          isValid = false;
        }
        if (!this.eventForm.description) {
          this.$set(this.eventForm.errors, "description", "Event description is required.");
          isValid = false;
        }
        if (!this.eventForm.category) {
          this.$set(this.eventForm.errors, "category", "Event category is required.");
          isValid = false;
        }
        if (!this.eventForm.venue) {
          this.$set(this.eventForm.errors, "venue", "Event venue is required.");
          isValid = false;
        }
        if (!this.eventForm.event_at) {
          this.$set(this.eventForm.errors, "event_at", "Event date is required.");
          isValid = false;
        }
        if (!this.eventForm.time) {
          this.$set(this.eventForm.errors, "time", "Event time is required.");
          isValid = false;
        }

        return isValid;
      },

      submitEventDetails() {
            this.loading = true;
            if (!this.validateEventForm()) {
                this.loading = false;
                return;
        }
            this.eventForm.errors = {};

            let formData = new FormData();
            formData.append("event_type", this.eventForm.event_type);
            formData.append("image", this.eventForm.image);
            formData.append("name", this.eventForm.name);
            if (this.eventForm.slug) {
                formData.append("slug", this.eventForm.slug);
            }
            formData.append("description", this.eventForm.description);
            formData.append("category", this.eventForm.category);
            formData.append("venue", this.eventForm.venue);
            formData.append("event_at", this.eventForm.event_at);
            formData.append("time", this.eventForm.time);

            axios
                .post("/business/event-onboarding/create-event", formData, {
                    headers: { "Content-Type": "multipart/form-data" }
                })
                .then(response => {
                    this.$toast.success("Event Created Successfully!", {
                        timeout: 3000
                    });
                    const createdEvent = response.data.body.model;
                    this.ticketForm.event_id = createdEvent.id;
                    this.eventCreated = true;

                })
                .catch(error => {
                    console.error("Error creating event:", error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        openTicketModal() {
            this.showTicketModal = true;
        },
// Update the saveTicket method to stringify the arrays
        saveTicket() {
            if (this.validateTicketForm()) {
                const ticketData = {
                    name: this.ticketForm.name,
                    type: this.ticketForm.type,
                    price: this.ticketForm.price,
                    description: this.ticketForm.description,
                    quantity: this.ticketForm.quantity,
                    days: this.ticketForm.days,
                    // Clean string formatting before saving
                    features: this.formatForStorage(this.ticketForm.features),
                    instructions: this.formatForStorage(this.ticketForm.instructions)
                };
                if (this.isEditing) {
                    this.$set(this.tickets, this.editingIndex, ticketData);
                    this.isEditing = false;
                    this.editingIndex = null;
                } else {
                    this.tickets.push(ticketData);
                }
                this.closeTicketModal();
            }
        },
        editTicket(index) {
            const ticket = this.tickets[index];
            this.ticketForm.name = ticket.name;
            this.ticketForm.type = ticket.type;
            this.ticketForm.price = ticket.price;
            this.ticketForm.description = ticket.description;
            this.ticketForm.quantity = ticket.quantity;
            this.ticketForm.days = ticket.days;
            this.isEditing = true;
            this.editingIndex = index;
            this.showTicketModal = true;
        },
        removeTicket(index) {
            this.tickets.splice(index, 1);
        },
        closeTicketModal() {
            this.showTicketModal = false;
            this.isEditing = false;
            this.editingIndex = null;
            this.ticketForm.name = "";
            this.ticketForm.type = "";
            this.ticketForm.price = "";
            this.ticketForm.description = "";
            this.ticketForm.quantity = "";
            this.ticketForm.days = 0;
            this.ticketForm.errors = {};
        },
        createTicketType() {
            if (!this.eventCreated) {
                alert("Please save event details before creating ticket types.");
                return;
            }
            if (this.tickets.length === 0) {
                alert("Please add at least one ticket type before saving.");
                return;
            }
            this.loading = true;
            this.ticketForm.tickets = this.tickets.map(ticket => ({
                ...ticket,
                features: ticket.features || JSON.stringify(this.defaultFeatures),
                instructions: ticket.instructions || JSON.stringify(this.defaultInstructions)
            }));

            axios
                .post("/business/event-onboarding/ticket-types", this.ticketForm)
                .then(response => {
                    this.$toast.success("Ticket Types Created Successfully!", {
                        timeout: 3000
                    });

                    // Redirect to events listing after 3 seconds (same as toast timeout)
                    setTimeout(() => {
                        this.$router.push("/business/events");
                    }, 3000);
                })
                .catch(error => {
                    console.error("Error creating ticket types:", error);
                    this.$toast.error("Failed to create ticket types. Please try again.", {
                        timeout: 3000
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
      // Method to validate event form

      validateTicketForm() {
            let isValid = true;
            this.ticketForm.errors = {};
            if (!this.ticketForm.name) {
                this.$set(this.ticketForm.errors, "name", "Ticket type name is required.");
                isValid = false;
            }
            if (!this.ticketForm.type) {
                this.$set(this.ticketForm.errors, "type", "Ticket type is required.");
                isValid = false;
            }
            if (!this.ticketForm.price || this.ticketForm.price <= 0) {
                this.$set(this.ticketForm.errors, "price", "Valid ticket price is required.");
                isValid = false;
            }
            if (!this.ticketForm.quantity || this.ticketForm.quantity <= 0) {
                this.$set(this.ticketForm.errors, "quantity", "Valid ticket quantity is required.");
                isValid = false;
            }
            if (!this.ticketForm.description) {
                this.$set(this.ticketForm.errors, "description", "Ticket description is required.");
                isValid = false;
            }
            if (this.ticketForm.type === 'Multi-Day Ticket' && (!this.ticketForm.days || this.ticketForm.days <= 0)) {
                this.$set(this.ticketForm.errors, "days", "Valid number of days is required.");
                isValid = false;
            }
            return isValid;
        },
        // Toggle editing mode for optional details
        toggleEditOptional() {
            this.editingOptional = !this.editingOptional;
            if (this.editingOptional) {
                // Pre-populate textareas with current custom values (if any)
                this.customFeaturesText = this.optionalDetails.features.join('\n');
                this.customInstructionsText = this.optionalDetails.instructions.join('\n');
            }
        },


        // Save custom features and instructions from the textareas
        saveOptionalDetails() {
            this.optionalDetails.features = this.customFeaturesText
                .split('\n')
                .map(item => item.trim())
                .filter(item => item.length > 0);
            this.optionalDetails.instructions = this.customInstructionsText
                .split('\n')
                .map(item => item.trim())
                .filter(item => item.length > 0);
            this.editingOptional = false;
            // Optionally, you can add an API call here to update the ticket types with these custom details.
        }
    }
};
</script>

<style>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
.cursor-pointer {
    cursor: pointer;
}
.alert {
    border-radius: 5px;
    margin-top: 20px;
}
.alert-success {
    padding: 5px 15px;
    font-size: 0.9rem;
    margin-bottom: 1rem;
}
.btn-primary {
    background-color: #252c65 !important;
    border-color: #252c65 !important;
}
.btn-primary:disabled,
.btn-primary.disabled {
    background-color: #5c5b5b !important;
    border-color: #5c5b5b !important;
    cursor: not-allowed;
    color: #fff;
}
</style>
